'use client';

import { useEffect, useState, type JSX } from 'react';
import { CircleHelp, LifeBuoy } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';

import {
  getFloatingActionPillState,
  type FloatingActionPillPlacement,
} from '@/components/floating-action-pill-state';
import { HoverLabelIconButton } from '@/components/home/org-thread-list-section-headers';
import { SIDEBAR_ICON_CLASS } from '@/components/home/sidebar-row-metrics';
import { PostHogSupportLauncher } from '@/components/support/posthog-support-launcher';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useWalkthrough } from '@/contexts/walkthrough-context';
import { useMediaQuery } from '@/hooks/use-media-query';
import { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';

const ALLOWED_PATHS = [
  '/design',
  '/avatar',
  '/reference',
  '/branding',
  '/brandings',
  '/aesthetics',
  '/settings',
];

const STRICT_PATHS = new Set([
  '/avatar',
  '/reference',
  '/branding',
  '/brandings',
  '/aesthetics',
]);

interface FloatingActionPillProps {
  collapsed?: boolean;
  placement?: FloatingActionPillPlacement;
}

function useVisibleThreadSidebarPill(enabled: boolean): boolean {
  const [hasVisiblePill, setHasVisiblePill] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const update = () => {
      const visible = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-floating-action-pill-placement="thread-sidebar"]'
        )
      ).some((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      });
      setHasVisiblePill(visible);
    };

    update();
    let updateFrame: number | null = null;
    const scheduleUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateFrame = null;
        update();
      });
    };
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'hidden', 'style'],
    });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      if (updateFrame !== null) window.cancelAnimationFrame(updateFrame);
    };
  }, [enabled]);

  return hasVisiblePill;
}

export function FloatingActionPill({
  collapsed = false,
  placement = 'floating',
}: FloatingActionPillProps = {}): JSX.Element | null {
  const pathname = usePathname();
  const params = useParams();
  const orgSlug = params?.orgSlug as string | undefined;
  const { hasWalkthroughForRoute, isActive, start } = useWalkthrough();
  const t = useTranslations('GlobalUI');
  const supportCopy = getPostHogSupportCopy(useLocale());
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isSidebarPlacement = placement === 'thread-sidebar';
  const hideBehindVisibleSidebarPill =
    useVisibleThreadSidebarPill(!isSidebarPlacement);

  const isFeedbackRoute = ALLOWED_PATHS.some((path) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const orgPath = orgSlug ? `/${orgSlug}${normalizedPath}` : null;

    if (STRICT_PATHS.has(normalizedPath)) {
      return pathname === normalizedPath || pathname === orgPath;
    }

    return (
      pathname === normalizedPath ||
      pathname.startsWith(`${normalizedPath}/`) ||
      (orgPath !== null &&
        (pathname === orgPath || pathname.startsWith(`${orgPath}/`)))
    );
  });

  const { helpAction, showFeedback } = getFloatingActionPillState({
    placement,
    hasWalkthroughForRoute,
    isWalkthroughActive: isActive,
    isFeedbackRoute,
  });

  if ((!helpAction && !showFeedback) || hideBehindVisibleSidebarPill) {
    return null;
  }

  const both = Boolean(helpAction) && showFeedback;
  const helpLabel = t('takeTour');
  const shouldStackAboveMobileFab =
    pathname === '/files/avatars' ||
    (orgSlug ? pathname === `/${orgSlug}/files/avatars` : false);
  const halfBase = isSidebarPlacement
    ? cn(
        'text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:bg-foreground/5 focus-visible:text-foreground focus-visible:ring-ring/40 flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-md px-1.5 text-[11px] font-medium transition-colors duration-150 outline-none focus-visible:ring-2 sm:min-h-7',
        // In the rail: one of its 36px buttons (44px touch target on phones).
        collapsed && 'size-11 px-0 sm:size-9 sm:rounded-[10px]'
      )
    : 'group/half relative flex size-11 items-center justify-center text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-primary/40 sm:size-10';
  const helpControlClassName = cn(
    halfBase,
    !isSidebarPlacement && (both ? 'rounded-s-full' : 'rounded-full')
  );
  const helpControlContent = (
    <>
      <CircleHelp
        className={cn(
          'shrink-0',
          isSidebarPlacement
            ? SIDEBAR_ICON_CLASS
            : 'size-5 transition-transform duration-150 group-hover/half:scale-110'
        )}
        aria-hidden
      />
      {isSidebarPlacement && !collapsed ? <span>{helpLabel}</span> : null}
    </>
  );

  if (isSidebarPlacement && !collapsed) {
    // Expanded sidebar footer: icon buttons that reveal their name on hover,
    // matching the section toolbars above.
    return (
      <div
        data-floating-action-pill-placement={placement}
        className={cn(
          'pointer-events-auto flex items-center gap-1 print:hidden',
          !helpAction && 'max-lg:hidden'
        )}
      >
        {helpAction ? (
          <HoverLabelIconButton
            size="sidebar"
            label={helpLabel}
            ariaLabel={helpLabel}
            onClick={start}
            data-walkthrough-help-button
          >
            <CircleHelp
              className={cn('text-sidebar-icon-foreground', SIDEBAR_ICON_CLASS)}
            />
          </HoverLabelIconButton>
        ) : null}
        {showFeedback ? (
          <div className="flex max-lg:hidden">
            <PostHogSupportLauncher
              accessibleLabel={supportCopy.title}
              renderTrigger={(triggerLabel) => (
                <HoverLabelIconButton
                  size="sidebar"
                  label={supportCopy.title}
                  ariaLabel={triggerLabel}
                  data-feedback-button="true"
                >
                  <LifeBuoy
                    className={cn(
                      'text-sidebar-icon-foreground',
                      SIDEBAR_ICON_CLASS
                    )}
                  />
                </HoverLabelIconButton>
              )}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-floating-action-pill-placement={placement}
      className={cn(
        isSidebarPlacement
          ? 'pointer-events-auto w-full print:hidden'
          : 'fixed end-4 z-50 print:hidden',
        // Feedback is desktop-only. Hiding in CSS keeps the server markup
        // stable; a pill whose only half is feedback disappears wholesale.
        !helpAction && 'max-lg:hidden',
        !isSidebarPlacement &&
          (shouldStackAboveMobileFab ? 'bottom-20 lg:bottom-4' : 'bottom-4')
      )}
    >
      <div
        className={cn(
          'flex items-center',
          isSidebarPlacement
            ? collapsed
              ? 'flex-col gap-1'
              : 'w-full justify-start gap-0.5 px-1'
            : 'border-border bg-background/90 rounded-full border shadow-lg backdrop-blur'
        )}
      >
        {helpAction ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={start}
                aria-label={helpLabel}
                data-walkthrough-help-button
                className={helpControlClassName}
              >
                {helpControlContent}
              </button>
            </TooltipTrigger>
            <TooltipContent side={collapsed ? 'right' : 'top'}>
              <span>{helpLabel}</span>
              {isDesktop ? (
                <span className="ms-1 opacity-70">· {t('tourHint')}</span>
              ) : null}
            </TooltipContent>
          </Tooltip>
        ) : null}

        {showFeedback ? (
          <PostHogSupportLauncher
            accessibleLabel={supportCopy.title}
            className={cn(
              halfBase,
              // Desktop-only half; see the container note above.
              'max-lg:hidden',
              !isSidebarPlacement &&
                (both
                  ? 'border-border rounded-e-full border-s'
                  : 'rounded-full')
            )}
          >
            <LifeBuoy
              className={cn(
                'shrink-0',
                isSidebarPlacement
                  ? SIDEBAR_ICON_CLASS
                  : 'size-5 transition-transform duration-150 group-hover/half:scale-110'
              )}
              aria-hidden
            />
            {isSidebarPlacement && !collapsed ? (
              <span className="whitespace-nowrap">{supportCopy.title}</span>
            ) : null}
          </PostHogSupportLauncher>
        ) : null}
      </div>
    </div>
  );
}
